import Link from 'next/link'

function isExternal(url: string) {
  try { return /^https?:\/\//i.test(url) } catch { return false }
}

function Cta({ cta }: { cta?: { label?: string; url?: string } }) {
  if (!cta?.label || !cta?.url) return null
  if (cta.url.startsWith('#')) {
    return (
      <a href={cta.url} className="inline-flex items-center gap-2 rounded border px-4 py-2 text-sm font-medium hover:bg-gray-50">{cta.label}</a>
    )
  }
  if (isExternal(cta.url)) {
    return (
      <a href={cta.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded border px-4 py-2 text-sm font-medium hover:bg-gray-50">{cta.label}</a>
    )
  }
  return (
    <Link href={cta.url} className="inline-flex items-center gap-2 rounded border px-4 py-2 text-sm font-medium hover:bg-gray-50">{cta.label}</Link>
  )
}

function BrandStory({ brandStory, productTitle }: any) {
  if (!brandStory?.headline || !brandStory?.body?.length) return null
  return (
    <section className="border bg-white px-5 py-6 shadow-sm">
      <div className="grid gap-5 lg:grid-cols-[1.1fr_1fr] lg:items-center">
        <div className="space-y-3">
          <span className="text-xs uppercase tracking-wide text-amber-600">Brand story</span>
          <h3 className="text-2xl font-semibold leading-snug">{brandStory.headline}</h3>
          <div className="space-y-3 text-sm text-gray-600">
            {brandStory.body.map((p: string, i: number) => (<p key={i}>{p}</p>))}
          </div>
        </div>
        {brandStory.image && (
          <div className="overflow-hidden border">
            <img src={brandStory.image} alt={`${productTitle ? productTitle + ' — ' : ''}${brandStory.headline}`} className="h-full w-full object-cover" loading="lazy" decoding="async" width="800" height="600" />
          </div>
        )}
      </div>
    </section>
  )
}

function StandardHero({ module, productTitle }: any) {
  return (
    <section className="relative overflow-hidden border shadow-sm">
      {module.backgroundImage && (
        <img src={module.backgroundImage} alt={`${productTitle ? productTitle + ' — ' : ''}${module.headline}`} className="absolute inset-0 h-full w-full object-cover" loading="lazy" decoding="async" width="1200" height="900" />
      )}
      <div className="relative bg-black/40">
        <div className="max-w-3xl px-5 py-12 text-white sm:px-10">
          <span className="text-xs uppercase tracking-widest text-white/70">Featured</span>
          <h3 className="mt-4 text-3xl font-semibold leading-tight">{module.headline}</h3>
          {module.subheadline && (<p className="mt-3 text-sm text-white/80">{module.subheadline}</p>)}
          {module.cta && (<div className="mt-5"><Cta cta={module.cta} /></div>)}
        </div>
      </div>
    </section>
  )
}

function StandardThreeImageText({ module, productTitle }: any) {
  return (
    <section className="border bg-white px-5 py-6 shadow-sm">
      <h3 className="text-xl font-semibold">{module.headline}</h3>
      <div className="mt-5 grid gap-5 md:grid-cols-3">
        {module.blocks?.map((b: any, i: number) => (
          <div key={i} className="space-y-3">
            <div className="border overflow-hidden">
              {b.image && (<img src={b.image} alt={`${productTitle ? productTitle + ' — ' : ''}${b.title}`} className="h-48 w-full object-cover" loading="lazy" decoding="async" width="600" height="450" />)}
            </div>
            <div className="space-y-2 text-sm text-gray-600">
              <h4 className="text-base font-semibold text-gray-900">{b.title}</h4>
              <p>{b.body}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  )
}

function StandardIconRow({ module, productTitle }: any) {
  return (
    <section className="border bg-white px-5 py-6 shadow-sm">
      <h3 className="text-xl font-semibold">{module.headline}</h3>
      <div className="mt-5 grid gap-4 sm:grid-cols-3">
        {module.icons?.map((icon: any, i: number) => (
          <div key={i} className="overflow-hidden border">
            {icon.image && (<img src={icon.image} alt={`${productTitle ? productTitle + ' — ' : ''}${icon.label}`} className="h-full w-full object-cover" loading="lazy" decoding="async" width="600" height="450" />)}
            <span className="sr-only">{icon.label}</span>
          </div>
        ))}
      </div>
    </section>
  )
}

function StandardImageHotspots({ module, productTitle }: any) {
  if (!module.image) return null
  return (
    <section className="border bg-white px-5 py-6 shadow-sm">
      <h3 className="text-xl font-semibold">{module.headline}</h3>
      <div className="mt-4 grid gap-4 lg:grid-cols-[1.15fr_0.85fr] lg:items-start">
        <div className="relative overflow-hidden border">
          <img src={module.image} alt={`${productTitle ? productTitle + ' — ' : ''}${module.headline}`} className="w-full object-cover" loading="lazy" decoding="async" width="1200" height="900" />
          {module.hotspots?.map((spot: any, i: number) => (
            <div key={i} className="absolute flex h-8 w-8 items-center justify-center rounded bg-black/80 text-xs font-semibold text-white" style={{ left: `${spot.x}%`, top: `${spot.y}%`, transform: 'translate(-50%, -50%)' }}>{i+1}</div>
          ))}
        </div>
        <div className="space-y-3 text-sm text-gray-600 lg:justify-self-center lg:max-w-md">
          {module.hotspots?.map((spot: any, i: number) => (
            <div key={i} className="flex items-start gap-3 bg-white px-4 py-3 shadow-sm ring-1 ring-gray-100">
              <span className="mt-0.5 flex h-6 w-6 items-center justify-center rounded bg-black text-xs font-semibold text-white">{i+1}</span>
              <div>
                <div className="font-semibold text-gray-900">{spot.title}</div>
                <p className="mt-1 text-gray-600">{spot.body}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

function StandardFourImageText({ module, productTitle }: any) {
  return (
    <section className="border bg-white px-5 py-6 shadow-sm">
      <h3 className="text-xl font-semibold">{module.headline}</h3>
      <div className="mt-5 grid gap-5 md:grid-cols-2">
        {module.tiles?.map((tile: any, i: number) => (
          <div key={i} className="space-y-3 border p-4">
            {tile.image && (
              <div className="overflow-hidden">
                <img src={tile.image} alt={`${productTitle ? productTitle + ' — ' : ''}${tile.title}`} className="h-48 w-full object-cover" loading="lazy" decoding="async" width="600" height="450" />
              </div>
            )}
            <div className="space-y-2 text-sm text-gray-600">
              <h4 className="text-base font-semibold text-gray-900">{tile.title}</h4>
              <p>{tile.body}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  )
}

function ComparisonTable({ comparisonTable, productTitle }: any) {
  if (!comparisonTable?.products?.length) return null
  const attributes = comparisonTable.attributes || []
  return (
    <section className="border bg-white px-5 py-6 shadow-sm">
      <h3 className="text-xl font-semibold">{comparisonTable.headline}</h3>
      <div className="mt-5 overflow-x-auto">
        <table className="min-w-full border-separate border-spacing-y-2 text-sm">
          <thead>
            <tr>
              <th className="w-40 text-left align-bottom text-xs font-semibold uppercase tracking-wide text-gray-600">Attribute</th>
              {comparisonTable.products.map((prod: any) => (
                <th key={prod.asin} className="min-w-[160px] text-left align-bottom">
                  <div className="border bg-gray-50 p-3">
                    {prod.image && (<img src={prod.image} alt={`${productTitle ? productTitle + ' — ' : ''}${prod.title}`} className="h-24 w-full object-cover" loading="lazy" decoding="async" width="320" height="240" />)}
                    <div className="mt-2 text-sm font-semibold text-gray-900">{prod.title}</div>
                    <div className="mt-1 text-xs text-gray-500">{prod.keyBenefit}</div>
                    <div className="mt-1 text-xs text-gray-500">Best for: {prod.bestFor}</div>
                    {prod.ctaLabel && prod.ctaUrl && (
                      <div className="mt-2">
                        {isExternal(prod.ctaUrl) ? (
                          <a href={prod.ctaUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center rounded border px-3 py-1 text-xs font-medium hover:bg-gray-100">{prod.ctaLabel}</a>
                        ) : (
                          <Link href={prod.ctaUrl} className="inline-flex items-center rounded border px-3 py-1 text-xs font-medium hover:bg-gray-100">{prod.ctaLabel}</Link>
                        )}
                      </div>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {attributes.map((attr: any, idx: number) => (
              <tr key={idx}>
                <td className="align-top text-sm font-medium text-gray-700">{attr.label}</td>
                {attr.values.map((v: any, i:number) => (
                  <td key={i} className="align-top text-sm text-gray-600">{v}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}

function renderModule(module: any, productTitle: string) {
  switch (module?.type) {
    case 'standardHero': return <StandardHero key={module.type + module.headline} module={module} productTitle={productTitle} />
    case 'standardThreeImageText': return <StandardThreeImageText key={module.type + module.headline} module={module} productTitle={productTitle} />
    case 'standardIconRow': return <StandardIconRow key={module.type + module.headline} module={module} productTitle={productTitle} />
    case 'standardImageHotspots': return <StandardImageHotspots key={module.type + module.headline} module={module} productTitle={productTitle} />
    case 'standardFourImageText': return <StandardFourImageText key={module.type + module.headline} module={module} productTitle={productTitle} />
    default: return null
  }
}

export default function APlusContent({ content, productTitle }: any) {
  if (!content) return null
  const rendered = (content.modules || []).map((m: any) => renderModule(m, productTitle)).filter(Boolean)
  return (
    <div className="space-y-6">
      <BrandStory brandStory={content.brandStory} productTitle={productTitle} />
      {rendered}
      <ComparisonTable comparisonTable={content.comparisonTable} productTitle={productTitle} />
    </div>
  )
}
